import { faChevronDown, faPlusCircle, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { useCallback, useEffect, useRef, useState } from "react";
import { useEditMode } from "../../hooks/useEditMode";
import { EDIT_FIELD, EDIT_SCOPE, MODULE_DESCRIPTION_ROWS } from "../../utils/constants";
import { handleMultilineInputKeyDown, handleSingleLineInputKeyDown, hasBeenEdited } from "../../utils/uiInteractionHelpers";
import ClickBoundary from "../ClickBoundary/ClickBoundary";
import EditButtonGroup from "../EditButton/EditButtonGroup";
import SubmoduleEditor from "../SubmoduleEditor/SubmoduleEditor";
import ModuleSettings from "../ModuleSettings/ModuleSettings";
import SortOrder from "../SortOrder/SortOrder";
import "./ModuleEditor.scss";
import { reorderContentItem, reorderContentItemsOnDelete } from "../../utils/sortOrderHelpers";
import { PackageType } from "features/Shared/ContentData/PackageType";

const COLOUR_WHITE = "#FFFFFF";
const ACCORDION_HEIGHT_BUFFER = 20;

const ModuleEditor = ({
	canEdit,
	module, // published
	currentDraft,
	index,
	total,
	onMove,
	onMoveTop,
	onMoveBottom,
	onChange,
	onDelete,
	onUpdateSubmoduleImageFile
}) => {
	const { setEditField, isEditing, cancelEditMode } = useEditMode();
	const [nameDraft, setNameDraft] = useState("");
	const [descDraft, setDescDraft] = useState("");
	const [isActiveDraft, setIsActiveDraft] = useState(false);
	const [isLockedLearningJourneyDraft, setIsLockedLearningJourneyDraft] = useState(false);
	const [isGuruShownDraft, setIsGuruShownDraft] = useState(false);
	const [isDeviceTrainingDraft, setIsDeviceTrainingDraft] = useState(false);
	const [submodulesDraft, setSubmodulesDraft] = useState(null);
	const tempSubmoduleIdRef = useRef(-1);

	const [isSubmodulesExpanded, setIsSubmodulesExpanded] = useState(false);
	const [submodulesContentHeight, setSubmodulesContentHeight] = useState(0);
	const submodulesInnerRef = useRef(null);

	const initialiseModuleDraftData = useCallback((draft, module) => {
		const draftActive = draft?.Active !== undefined ? Boolean(draft.Active) : Boolean(module?.Active);
		const draftLockedLearningJourney = draft?.Unlocked !== undefined ? !draft.Unlocked : !module?.Unlocked;
		const draftShowGuru = draft?.ShowGuru !== undefined ? Boolean(draft.ShowGuru) : Boolean(module?.ShowGuru);
		const draftDeviceTraining = draft?.DeviceTraining !== undefined ? Boolean(draft.DeviceTraining) : Boolean(module?.DeviceTraining);

		setNameDraft(draft?.[EDIT_FIELD.NAME] ?? module.Name ?? "");
		setDescDraft(draft?.[EDIT_FIELD.DESCRIPTION] ?? module.Description ?? "");
		setIsActiveDraft(draftActive);
		setIsLockedLearningJourneyDraft(draftLockedLearningJourney);
		setIsGuruShownDraft(draftShowGuru);
		setIsDeviceTrainingDraft(draftDeviceTraining);
		
		// This indirectly causes the useEffect in EditableSubmodule.jsx to initialise data.
		const draftSubmodules = draft.Submodules ? structuredClone(draft.Submodules) : structuredClone(module.Submodules ?? []);
		setSubmodulesDraft(draftSubmodules);
	}, []);

	// Hook to initialise input field information for editing.
	useEffect(() => {
		if (currentDraft || module) {
			initialiseModuleDraftData(currentDraft, module);
		}
	}, [initialiseModuleDraftData, currentDraft, module]);

	// Measure the submodules content height whenever submodulesDraft changes
	useEffect(() => {
		if (submodulesInnerRef.current) {
			setSubmodulesContentHeight(submodulesInnerRef.current.scrollHeight);
		}
	}, [submodulesDraft]);

	/**
	 * Required fields within ModuleDataDto:
	 * Id (int)
	 * Name (string)
	 * Position (int)
	 * Description (string)
	 * Active (bool)
	 * Unlocked (bool)
	 * ShowGuru (bool)
	 * DeviceTraining (bool)
	 * Submodules (List<SubmoduleDataDto>)
	 */
	const getModuleDtoDraft = (overrides = {}) => ({
		...module,
		[EDIT_FIELD.NAME]: nameDraft,
		[EDIT_FIELD.DESCRIPTION]: descDraft,
		[EDIT_FIELD.ACTIVE]: isActiveDraft,
		Unlocked: !isLockedLearningJourneyDraft,
		ShowGuru: isGuruShownDraft,
		DeviceTraining: isDeviceTrainingDraft,
		...overrides
	});

	const handleBlur = (override) => {
		onChange(getModuleDtoDraft(override));
	}

	const handleActiveChange = () => {
		const newValue = !isActiveDraft;
		setIsActiveDraft(newValue);
		onChange(
			getModuleDtoDraft({
				Active: newValue
			})
		);
	};

	const handleLockedLearningJourneyChange = () => {
		const newValue = !isLockedLearningJourneyDraft;
		setIsLockedLearningJourneyDraft(!newValue);
		onChange(
			getModuleDtoDraft({
				Unlocked: !newValue
			})
		);
	};

	const handleShowGuruChange = () => {
		const newShowGuru = !isGuruShownDraft;
		const newDeviceTraining = newShowGuru ? false : isDeviceTrainingDraft;

		setIsGuruShownDraft(newShowGuru);
		setIsDeviceTrainingDraft(newDeviceTraining);

		onChange(
			getModuleDtoDraft({
				ShowGuru: newShowGuru,
				DeviceTraining: newDeviceTraining
			})
		);
	};

	const handleDeviceTrainingChange = () => {
		const newDeviceTraining = !isDeviceTrainingDraft;
		const newShowGuru = newDeviceTraining ? false : isGuruShownDraft;

		setIsDeviceTrainingDraft(newDeviceTraining);
		setIsGuruShownDraft(newShowGuru);

		onChange(
			getModuleDtoDraft({
				DeviceTraining: newDeviceTraining,
				ShowGuru: newShowGuru
			})
		);
	};

	const handleAddSubmodule = () => {
		const newSubmodule = {
			Id: tempSubmoduleIdRef.current,
			Name: "",
			Description: "",
			Keywords: "",
			Duration: "",
			Content: {
				StoredName: "",
				UploadDate: "",
				FileSize: 0,
				PackageType: PackageType.Invalid
			},
			ImageUrl: "",
			ImageFile: null,
			HasProgress: true,
			Position: submodulesDraft.length,
		};
		const updatedSubmodules = [...submodulesDraft, newSubmodule];
		setSubmodulesDraft(updatedSubmodules);
		onChange(getModuleDtoDraft({ Submodules: updatedSubmodules }));

		// Decrement Id reference for next created Module.
		tempSubmoduleIdRef.current -= 1;
	};

	const handleDeleteSubmodule = (moduleId) => {
		let updatedDraftSubmodules = submodulesDraft.filter(m => m.Id !== moduleId);
		updatedDraftSubmodules = reorderContentItemsOnDelete(updatedDraftSubmodules);
		setSubmodulesDraft(updatedDraftSubmodules);
		onChange(getModuleDtoDraft({ Submodules: updatedDraftSubmodules }));
	};

	const handleChangeSubmodule = (updatedSubmodule) => {
		const updatedDraftSubmodules = submodulesDraft.map((m) =>
			m.Id === updatedSubmodule.Id ? updatedSubmodule : m
		);
		setSubmodulesDraft(updatedDraftSubmodules);
		onChange(getModuleDtoDraft({ Submodules: updatedDraftSubmodules }));
	};

	const handleChangeSubmodulePosition = (submoduleId, to) => {
		const renumbered = reorderContentItem(submodulesDraft, submoduleId, to);
		setSubmodulesDraft(renumbered);
		onChange(getModuleDtoDraft({ Submodules: renumbered }));
	};

	const fieldGetters = {
		[EDIT_FIELD.NAME]: () => nameDraft,
		[EDIT_FIELD.DESCRIPTION]: () => descDraft
	};

	const handleEditField = (field) => {
		setEditField({
			scope: EDIT_SCOPE.MODULE,
			id: module.Id,
			field: field
		});
	};

	const handleUndoField = (field) => {
		const fieldSetters = {
			[EDIT_FIELD.NAME]: () => setNameDraft,
			[EDIT_FIELD.DESCRIPTION]: () => setDescDraft
		};
		const publishedValue = module?.[field] ?? "";
		fieldSetters[field](publishedValue);
		onChange(
			getModuleDtoDraft({
				[field]: publishedValue
			})
		);
		cancelEditMode();
	};

	const getPublishedSubmodule = (m) => {
		const current = module.Submodules?.find((x) => x.Id === m.Id);
		if (current) {
			return current;
		}
		const unpublishedDraft = currentDraft.Submodules?.find((x) => x.Id === m.Id);
		if (unpublishedDraft) {
			return unpublishedDraft;
		}
		return {
			Id: m.Id
		};
	};

	const handleToggleSubmodules = () => {
		setIsSubmodulesExpanded((prev) => !prev);
	};

	const calculatedSubmodulesMaxHeight = isSubmodulesExpanded
		? submodulesContentHeight + ACCORDION_HEIGHT_BUFFER
		: 0;

	return (
		<div className="editable-module">
			<div className="editable-module-sidebar">
				<SortOrder
					index={index}
					total={total}
					onMove={onMove}
					onMoveTop={onMoveTop}
					onMoveBottom={onMoveBottom}
					label="Module Position"
				/>
				<div className="editable-module-sidebar-delete" onClick={() => onDelete(module.Id)}>
					<FontAwesomeIcon icon={faTrashCan} />
					<p>
						Delete
						<br />
						Module
					</p>
				</div>
			</div>

			<div className="editable-module-main">
				{/* Title */}
				<ClickBoundary>
					{isEditing(EDIT_SCOPE.MODULE, EDIT_FIELD.NAME, module.Id) ? (
						<InputText
							autoFocus
							maxLength={150}
							value={nameDraft}
							onChange={(event) => setNameDraft(event.target.value)}
							onKeyDown={(e) => handleSingleLineInputKeyDown(e, cancelEditMode)}
							onBlur={() => handleBlur({ [EDIT_FIELD.NAME]: nameDraft })}
							className="title-textbox"
						/>
					) : (
						<div className="editable-module-main-title">
							<h2>{nameDraft || "Add Name"}</h2>
							<EditButtonGroup
								show={canEdit}
								onEdit={() => handleEditField(EDIT_FIELD.NAME)}
								onUndo={() => handleUndoField(EDIT_FIELD.NAME)}
								showUndo={hasBeenEdited(module, fieldGetters, EDIT_FIELD.NAME)}
							/>
						</div>
					)}
				</ClickBoundary>

				{/* Description */}
				<ClickBoundary>
					{isEditing(EDIT_SCOPE.MODULE, EDIT_FIELD.DESCRIPTION, module.Id) ? (
						<InputTextarea
							maxLength={3000}
							value={descDraft}
							onChange={(event) => setDescDraft(event.target.value)}
							onKeyDown={(e) => handleMultilineInputKeyDown(e, cancelEditMode)}
							onBlur={() => handleBlur({ [EDIT_FIELD.DESCRIPTION]: descDraft })}
							rows={MODULE_DESCRIPTION_ROWS}
							autoResize
							className="description-textbox"
						/>
					) : (
						<div className="editable-module-main-description">
							<p>{descDraft || "Add Description"}</p>
							<EditButtonGroup
								show={canEdit}
								onEdit={() => handleEditField(EDIT_FIELD.DESCRIPTION)}
								onUndo={() => handleUndoField(EDIT_FIELD.DESCRIPTION)}
								showUndo={hasBeenEdited(module, fieldGetters, EDIT_FIELD.DESCRIPTION)}
							/>
						</div>
					)}
				</ClickBoundary>

				<ModuleSettings
					active={isActiveDraft}
					onChangeActive={handleActiveChange}
					lockedLearningJourney={isLockedLearningJourneyDraft}
					onChangeLockedLearningJourney={handleLockedLearningJourneyChange}
					showGuru={isGuruShownDraft}
					onChangeShowGuru={handleShowGuruChange}
					deviceTraining={isDeviceTrainingDraft}
					onChangeDeviceTraining={handleDeviceTrainingChange}
				/>

				<button
					type="button"
					className="editable-module-accordion-toggle"
					onClick={handleToggleSubmodules}
					aria-expanded={isSubmodulesExpanded}
				>
					<h3>Submodules</h3>
					<FontAwesomeIcon
						icon={faChevronDown}
						className={
							isSubmodulesExpanded
								? "editable-module-accordion-chev editable-module-accordion-chev--up"
								: "editable-module-accordion-chev"
						}
					/>
				</button>
				<div
					className="editable-submodules"
					style={{ maxHeight: `${calculatedSubmodulesMaxHeight}px` }}
				>
					<div ref={submodulesInnerRef}>
						{module &&
							module.Submodules &&
							Array.isArray(submodulesDraft) &&
							submodulesDraft
								.slice()
								.sort((a, b) => (a.Position ?? 0) - (b.Position ?? 0))
								.map((item, index, sortedSubmodules) => (
									<SubmoduleEditor
										key={`${item.Id}-${item.Position}`}
										canEdit={canEdit}
										moduleId={module.Id}
										deviceTraining={isDeviceTrainingDraft}
										submodule={getPublishedSubmodule(item)}
										currentDraft={currentDraft.Submodules?.find((dm) => dm.Id === item.Id) ?? {}}
										index={index}
										total={sortedSubmodules.length}
										onMove={(to) => handleChangeSubmodulePosition(item.Id, to)}
										onMoveTop={() => handleChangeSubmodulePosition(item.Id, 0)}
										onMoveBottom={() => handleChangeSubmodulePosition(item.Id, submodulesDraft.length - 1)}
										onChange={handleChangeSubmodule}
										onDelete={handleDeleteSubmodule}
										onUpdateSubmoduleImageFile={onUpdateSubmoduleImageFile}
									/>
								))}
					</div>
				</div>

				<div className="editable-module-main-addnew">
					<div className="editable-module-main-addnew-button" onClick={handleAddSubmodule}>
						<p>
							Add New Submodule{" "}
							<FontAwesomeIcon icon={faPlusCircle} style={{ color: COLOUR_WHITE }} />
						</p>
					</div>
					<div className="editable-module-main-addnew-row">
						<div className="editable-module-main-addnew-button">
							<p>
								Add Existing Submodule{" "}
								<FontAwesomeIcon icon={faPlusCircle} style={{ color: COLOUR_WHITE }} />
							</p>
						</div>
						<div className="editable-module-main-addnew-button">
							<p>
								Add New Assessment{" "}
								<FontAwesomeIcon icon={faPlusCircle} style={{ color: COLOUR_WHITE }} />
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ModuleEditor;
